import React, { useMemo } from 'react'
import {
  useGetTimeOffBalanceQuery,
  useGetTimeOffEntitlementQuery,
  useHandleTimeOffOffboardingMutation,
} from '@/features/time-off/time-off.api.ts'
import { ColDef } from 'ag-grid-community'
import {
  TimeOffAllocation,
  TimeOffAllocationStatus,
  TimeOffOnboardingPreviewItem,
} from '@/features/time-off/time-off.def.ts'
import { Alert, Button, Flex, notification, Popconfirm, Tag } from 'antd'
import { CustomAgGrid } from '@/common/components/CustomAgGrid.tsx'
import { toInclusiveDateOnly } from '@/pages/time-off/utils/time-off-display.ts'
import { cn } from '@/utils/style.ts'
import { handleApiError } from '@/utils/api.tsx'
import { OffboardingRecord } from '@/features/offboarding/offboarding.def.ts'

type TimeOffEntitlementInfoProps = {
  employeeId: string
  offboardingRecord: OffboardingRecord
}

// previewItem -> use time off type id -> add entitleAmount to the allocation with same time off type id & isMain = true
type TimeOffEntitlement = TimeOffAllocation & {
  entitledAmount: TimeOffOnboardingPreviewItem['proratedAmount'] | undefined
}

const TimeOffEntitlementInfo: React.FC<TimeOffEntitlementInfoProps> = ({ employeeId, offboardingRecord }) => {
  const terminationDate = offboardingRecord.details.terminationDate
  const { data: balanceResponse, isFetching: isBalanceFetching, refetch: refetchBalances } = useGetTimeOffBalanceQuery(
    { employeeId: employeeId || '' },
    { skip: !employeeId },
  )
  const timeOffBalances = balanceResponse?.balances
  const {
    data: currentEntitlement,
    isFetching: isEntitlementFetching,
    refetch: refetchEntitlement,
  } = useGetTimeOffEntitlementQuery({
      employeeId: employeeId || '', targetEndDate: terminationDate!,
    }, { skip: !employeeId || !terminationDate },
  )
  const [triggerOffboarding, { isLoading: isOffboardingLoading }] = useHandleTimeOffOffboardingMutation()

  const timeOffTypeNameDict = (timeOffBalances || []).map(x => x.timeOffType).reduce((acc, x) => ({
    ...acc,
    [x.id]: x.name,
  }), {}) as Record<number, string>

  const handleOffboarding = async () => {
    if (!employeeId) return

    try {
      await triggerOffboarding({ employeeId: employeeId }).unwrap()
      notification.success({ message: 'Time off offboarding completed successfully' })
    } catch (error) {
      handleApiError(error)
    }
  }

  const timeOffEntitlements = useMemo<TimeOffEntitlement[]>(() => {
    const entitledAmountByTimeOffTypeId = new Map(
      currentEntitlement?.items.map(item => [item.timeOffTypeId, item.proratedAmount]) ?? [],
    )

    return timeOffBalances?.flatMap(balance => {
      return balance.timeOffAllocations.map(allocation => ({
        ...allocation,
        entitledAmount: allocation.isMain
          ? entitledAmountByTimeOffTypeId.get(allocation.timeOffTypeId)
          : allocation.status === TimeOffAllocationStatus.Active
            ? allocation.grantedAmount
            : 0,
      }))
    }) ?? []
  }, [currentEntitlement?.items, timeOffBalances])

  const columnDefs: ColDef<TimeOffEntitlement>[] = useMemo(() => {
    return [
      {
        colId: 'timeOffTypeName',
        headerName: 'Time Off Type Name',
        valueGetter: ({ data }) => data ? timeOffTypeNameDict[data.timeOffTypeId] : null,
        filter: true,
      },
      {
        colId: 'name',
        field: 'name',
        headerName: 'Allocation Name',
        filter: true,
      },
      {
        colId: 'grantedAmount',
        field: 'grantedAmount',
        headerName: 'Granted',
        filter: true,
      },
      {
        colId: 'consumedAmount',
        field: 'consumedAmount',
        headerName: 'Consumed',
        filter: true,
      },
      {
        colId: 'availableAmount',
        field: 'availableAmount',
        headerName: 'Unconsumed Amount',
        filter: true,
      },
      {
        colId: 'entitledAmount',
        field: 'entitledAmount',
        headerName: 'Remaining Entitled Amount',
        filter: true,
        valueGetter: ({ data }: { data?: TimeOffEntitlement }) => {
          if (!data || data.entitledAmount == undefined) return 0
          if (data.status != TimeOffAllocationStatus.Active) return 0
          return data.entitledAmount - data.consumedAmount
        },
        cellClass: ({ value }: { value?: number }) => {
          if (value == undefined) return ''
          if (value < 0) return 'text-red-500 font-bold'
          return ''
        },
      },
      {
        colId: 'localExpiryTime',
        field: 'localExpiryTime',
        headerName: 'Expiry',
        valueGetter: (params) => {
          return toInclusiveDateOnly(params.data?.localExpiryTime)
        },
        filter: true,
      },
      {
        colId: 'status',
        field: 'status',
        headerName: 'Status',
        cellRenderer: ({ value }: { value: string }) => {
          if (!value) return null
          return <Tag
            className={cn({
              'bg-green-100 text-green-800': value === TimeOffAllocationStatus.Active,
              'bg-gray-100 text-gray-800': value === TimeOffAllocationStatus.Inactive,
              'bg-orange-100 text-orange-800': value === TimeOffAllocationStatus.ExpiryProcessed,
            })}
          >{value}</Tag>
        },
        filter: true,
      },
    ]
  }, [timeOffTypeNameDict])

  const handleRefetch = () => {
    refetchBalances()
    refetchEntitlement()
  }

  return (
    <Flex vertical gap="small">
      {employeeId ? (
        <Alert
          showIcon
          type="info"
          className="w-fit"
          message={`Entitlements are calculated based on 1st Jan/the first day in current country until termination date: ${currentEntitlement?.refStartDate} - ${currentEntitlement?.refEndDate}`}
        />
      ) : null}
      <CustomAgGrid
        rowData={timeOffEntitlements}
        refetch={handleRefetch}
        isLoading={isBalanceFetching || isEntitlementFetching}
        columnDefs={columnDefs}
        gridOptions={{ rowSelection: undefined }}
        toolbarExtra={
          <Popconfirm
            title="Are you sure to detach time off policy? This action will turn all active allocations to inactive and cannot be undone."
            onConfirm={handleOffboarding}
            okText="Yes"
            cancelText="No"
          >
            <Button danger loading={isOffboardingLoading}>Detach Policy</Button>
          </Popconfirm>
        }
      />
    </Flex>
  )
}

export default TimeOffEntitlementInfo